import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, BellOff, Check, CircleAlert, Loader2 } from 'lucide-react';
import { useSiteTheme } from '../hooks/useSiteTheme';
import { unsubscribeByToken } from '../lib/authApi';
import { apiErrorMessage } from '../lib/projectApi';
import { WannaPublishLockup } from '../components/editor/WannaPublishLogo';
import { Button } from '../components/ui/button';

const heading = { fontFamily: 'Outfit, Inter, sans-serif' };

/**
 * /unsubscribe?token=… — where the "Unsubscribe" link in every tips / product-update email lands. One tap confirms
 * (the link itself only redirects here, so a mail scanner following links cannot unsubscribe anyone). Account emails
 * (receipts, password resets) are never affected; the switch to come back is in Profile settings.
 */
export default function Unsubscribe() {
  const location = useLocation();
  const navigate = useNavigate();
  const [theme] = useSiteTheme();
  const token = new URLSearchParams(location.search).get('token') || '';
  const [state, setState] = useState(token ? 'ready' : 'invalid'); // ready | busy | done | invalid
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { document.title = 'Unsubscribe · WannaPublish'; }, []);

  const confirm = async () => {
    if (state === 'busy') return;
    setState('busy');
    setError('');
    try {
      const r = await unsubscribeByToken(token);
      setEmail(r.email || '');
      setState('done');
    } catch (e) {
      if (e?.response?.status === 404) { setState('invalid'); return; }
      setError(await apiErrorMessage(e, 'That did not work — please try again.'));
      setState('ready');
    }
  };

  return (
    <main className="min-h-screen bg-[hsl(var(--wp-workspace))] text-foreground flex flex-col wp-landing" data-testid="unsubscribe-page" data-theme={theme}>
      <header className="flex items-center justify-between px-6 py-5 sm:px-10">
        <Link to="/" className="flex items-center gap-2.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="unsubscribe-brand-link">
          <WannaPublishLockup size={36} className="shrink-0" />
        </Link>
        <Button type="button" variant="ghost" size="sm" className="h-9 text-sm" onClick={() => navigate('/')} data-testid="unsubscribe-back"><ArrowLeft className="h-4 w-4 mr-1.5" aria-hidden="true" />Back to WannaPublish</Button>
      </header>

      <section className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-[440px]">
          <div className="mb-6">
            <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-card shadow-sm border border-border" aria-hidden="true"><BellOff className="h-6 w-6 text-primary" /></div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight leading-tight" style={heading} data-testid="unsubscribe-title">
              {state === 'done' ? 'You are unsubscribed' : state === 'invalid' ? 'This link is not valid' : 'Unsubscribe from tips & product updates?'}
            </h1>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 sm:p-6 shadow-sm space-y-4" data-testid="unsubscribe-card">
            {state === 'done' && (
              <>
                <p className="text-sm text-foreground flex items-start gap-2" data-testid="unsubscribe-done"><Check className="h-4 w-4 shrink-0 text-primary mt-0.5" aria-hidden="true" /><span>No more tips or product updates will be sent to <span className="font-medium">{email || 'your address'}</span>.</span></p>
                <p className="text-xs text-muted-foreground">Account emails — receipts, password resets, billing notices — are not affected. Changed your mind? Turn the switch back on under <strong>Account ▸ Profile settings ▸ Email preferences</strong>.</p>
                <Button type="button" variant="outline" className="w-full h-11 text-sm" onClick={() => navigate('/app')} data-testid="unsubscribe-open-app">Open WannaPublish</Button>
              </>
            )}
            {state === 'invalid' && (
              <>
                <p className="text-sm text-foreground flex items-start gap-2" data-testid="unsubscribe-invalid"><CircleAlert className="h-4 w-4 shrink-0 text-primary mt-0.5" aria-hidden="true" /><span>This unsubscribe link is incomplete or no longer valid.</span></p>
                <p className="text-xs text-muted-foreground">You can always change what we send you from <strong>Account ▸ Profile settings ▸ Email preferences</strong> after signing in.</p>
                <Button type="button" className="w-full h-11 text-sm font-semibold" onClick={() => navigate('/app')} data-testid="unsubscribe-sign-in">Sign in to manage email preferences</Button>
              </>
            )}
            {(state === 'ready' || state === 'busy') && (
              <>
                <p className="text-sm text-muted-foreground">You will stop receiving the optional tips and product-update emails. Account emails — receipts, password resets, billing notices — keep coming as usual.</p>
                {error && <p role="alert" className="text-xs text-destructive" data-testid="unsubscribe-error">{error}</p>}
                <Button type="button" className="w-full h-11 text-sm font-semibold" onClick={confirm} disabled={state === 'busy'} data-testid="unsubscribe-confirm">
                  {state === 'busy' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" /> : <BellOff className="h-4 w-4 mr-2" aria-hidden="true" />}{state === 'busy' ? 'One moment…' : 'Yes, unsubscribe me'}
                </Button>
              </>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
